import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './context/AuthContext';
import queryClient from './utils/queryClient';
import App from './App';
import './index.css';

// ── Sentry error tracking (optional — enable via REACT_APP_SENTRY_DSN) ──
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV,
    tracesSampleRate: 0.05,
  });
}

// Inline fallback: a render throw anywhere used to white-screen the whole ATS
// with no recovery path. Kept markup-only on purpose — this file must not pull
// in page or layout modules.
function AppCrashFallback({ error, resetError }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
        <h1 className="text-lg font-semibold text-slate-900">Something broke on this screen</h1>
        <p className="mt-2 text-sm text-slate-600">
          The error was reported. Your data was not lost — reload to continue where you left off.
        </p>
        {process.env.NODE_ENV !== 'production' && error?.message && (
          <pre className="mt-4 text-left text-xs text-rose-700 bg-rose-50 border border-rose-100 rounded-lg p-3 overflow-auto max-h-40">
            {error.message}
          </pre>
        )}
        <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={() => {
              if (typeof resetError === 'function') resetError();
              window.location.assign('/admin/dashboard');
            }}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={AppCrashFallback}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: { borderRadius: '12px', padding: '12px 16px', fontSize: '14px' },
              }}
            />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
